import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { getTenantFromHostname } from '@config/tenant'
import usePreferencesStore from '@store/preferencesStore'
import userService from '@api/v1/userService'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'
import { useAiMode, useShowDashboards, useUpdateUserSettings } from '@hooks/useUserSettings'
import { REPORTS_ONLY_MODE, HOME_ROUTE, DASHBOARDS_TAB, COLUMN_ALIASES } from '@config/featureFlags'
import ColumnAliasSettings from '@components/reports/ColumnAliasSettings'
import FirmDashboardsSetting from '@components/reports/FirmDashboardsSetting'

const PAGE_SIZES = [25, 50, 100, 200]
const THEMES = ['light', 'system']
const AI_MODE_OPTIONS = [
  { id: 'off', label: 'Off', desc: 'No AI features on reports.' },
  { id: 'summary', label: 'AI Summary', desc: 'A one-click, plain-language summary of the report.' },
  { id: 'chat', label: 'AI Chat', desc: 'Ask questions about the report, with suggested questions each turn.' },
]

const Settings = () => {
  useDocumentTitle('Settings')
  const { user } = useAuth0()
  const tenant = getTenantFromHostname()
  const { data: meData } = useQuery({
    queryKey: ['users-me'],
    queryFn: () => userService.getMe(),
    retry: false,
  })
  const displayName = meData?.data?.displayName || user?.name || 'Unknown'
  const email = meData?.data?.email || user?.email || ''
  const aiMode = useAiMode()
  const showDashboards = useShowDashboards()
  const updateSettings = useUpdateUserSettings()
  const {
    sidebarCollapsed,
    setSidebarCollapsed,
    defaultPageSize,
    setDefaultPageSize,
    theme,
    setTheme,
  } = usePreferencesStore()

  return (
    <div className="max-w-3xl mx-auto">
      <Link
        to={HOME_ROUTE}
        className="inline-flex items-center gap-1.5 text-sm text-cb-accent-text hover:text-cb-navy mb-4"
      >
        <span aria-hidden="true">←</span>
        Back to {REPORTS_ONLY_MODE ? 'Report Center' : 'Dashboard'}
      </Link>
      <div className="mb-8">
        <h1 className="text-2xl font-display text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Account and application settings</p>
      </div>

      {/* AI Assistant Section — the primary, interactive setting; leads the page. */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">AI Assistant</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            How AI appears on report views · saved to your account (this tenant)
          </p>
        </div>
        <div className="p-6">
          <div role="radiogroup" aria-label="AI assistant mode" className="space-y-2">
            {AI_MODE_OPTIONS.map((o) => {
              const selected = aiMode === o.id
              return (
                <label
                  key={o.id}
                  className={`flex items-start gap-3 p-3 rounded-cb border cursor-pointer transition-colors ${
                    selected ? 'border-cb-accent bg-cb-light/50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="ai-mode"
                    value={o.id}
                    checked={selected}
                    onChange={() => updateSettings.mutate({ aiMode: o.id })}
                    className="mt-0.5 text-cb-accent-text focus:ring-cb-accent"
                  />
                  <span>
                    <span className="block text-sm font-medium text-cb-navy">{o.label}</span>
                    <span className="block text-xs text-gray-500">{o.desc}</span>
                  </span>
                </label>
              )
            })}
          </div>
          {updateSettings.isError && (
            <p className="text-xs text-red-600 mt-3">Couldn’t save — try again.</p>
          )}
        </div>
      </div>

      {/* Preferences Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Preferences</h2>
          <p className="text-xs text-gray-500 mt-0.5">Changes are saved automatically</p>
        </div>
        <div className="p-6 space-y-5">
          {/* Sidebar default — hidden in reports-only mode (there is no sidebar to collapse). */}
          {!REPORTS_ONLY_MODE && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Sidebar collapsed by default</p>
                <p className="text-xs text-gray-500">Start with the sidebar collapsed on load</p>
              </div>
              <button
                aria-label="Toggle sidebar collapsed by default"
                role="switch"
                aria-checked={sidebarCollapsed}
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  sidebarCollapsed ? 'bg-cb-accent' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                    sidebarCollapsed ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}

          {/* Default page size */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Default page size</p>
              <p className="text-xs text-gray-500">Number of rows shown in data grids</p>
            </div>
            <select
              aria-label="Default page size"
              value={defaultPageSize}
              onChange={(e) => setDefaultPageSize(Number(e.target.value))}
              className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>{size} rows</option>
              ))}
            </select>
          </div>

          {/* Theme — placeholder until dark mode ships; hidden in reports-only mode. */}
          {!REPORTS_ONLY_MODE && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Theme</p>
                <p className="text-xs text-gray-500">Visual appearance (dark mode coming soon)</p>
              </div>
              <select
                aria-label="Theme"
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent"
              >
                {THEMES.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Report Center — Dashboards opt-in (spec 038). Only shown where the feature
          is shipped (build flag on); per-user, server-persisted. */}
      {DASHBOARDS_TAB && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Report Center</h2>
            <p className="text-xs text-gray-500 mt-0.5">Saved to your account (this tenant)</p>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Dashboards
                  <span className="text-[10px] font-semibold uppercase tracking-wide align-middle ml-1.5 px-1.5 py-0.5 rounded-full bg-cb-accent/10 text-cb-accent-text">Beta</span>
                </p>
                <p className="text-xs text-gray-500">Show the Dashboards view in the Report Center — it opens on Dashboards by default.</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={showDashboards}
                aria-label="Show Dashboards in the Report Center"
                onClick={() => updateSettings.mutate({ showDashboards: !showDashboards })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showDashboards ? 'bg-cb-accent' : 'bg-gray-200'
                }`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                  showDashboards ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            {updateSettings.isError && (
              <p className="text-xs text-red-600 mt-3">Couldn’t save — try again.</p>
            )}

            <FirmDashboardsSetting />
          </div>
        </div>
      )}

      {/* Per-firm column names (spec 061). Gated HERE as well as inside, and
          that is load-bearing rather than belt-and-braces: the component's own
          `if (!COLUMN_ALIASES) return null` runs *after* its hooks, so mounting
          it with the flag off still costs this page a QueryClientProvider it
          has never needed. Gating at the call site keeps a default-off feature
          genuinely inert. */}
      {COLUMN_ALIASES && <ColumnAliasSettings />}

      {/* Profile Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Profile</h2>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-cb-purple flex items-center justify-center text-white text-xl font-semibold">
              {displayName[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900">{displayName}</p>
              <p className="text-sm text-gray-500">{email}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InfoField label="Name" value={displayName} />
            <InfoField label="Email" value={email} />
            <InfoField label="Nickname" value={user?.nickname} />
            <InfoField label="Email Verified" value={user?.email_verified ? 'Yes' : 'No'} />
          </div>
        </div>
      </div>

      {/* Tenant Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Tenant</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InfoField label="Tenant ID" value={tenant} />
            <InfoField label="Environment" value={window.location.hostname.includes('localhost') ? 'Development' : 'Production'} />
            <InfoField label="App URL" value={window.location.origin} />
            <InfoField label="API Base" value="/api/v1" />
          </div>
        </div>
      </div>

      {/* About Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">About</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InfoField label="Application" value="Centerbase Next-Gen" />
            <InfoField label="Version" value={`2.0.0-beta (${__APP_VERSION__})`} />
            <InfoField label="Built" value={__BUILD_TIME__} />
            <InfoField label="Platform" value="Cloudflare Pages" />
            <InfoField label="Framework" value="React 19 + Vite" />
          </div>
        </div>
      </div>
    </div>
  )
}

export const UsersSection = () => {
  const [resetTarget, setResetTarget] = useState(null)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [message, setMessage] = useState(null)

  // Held as an object, not destructured booleans, so `useReadState` can read
  // `fetchStatus` — `isLoading` alone cannot tell "still loading" from "never asked".
  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getUsers({ limit: 200 }),
    retry: false,
  })
  const { data, error, refetch } = usersQuery

  /*
   * Three read outcomes, not two — see `@api/v1/readState`.
   *
   * `isLoading` is `isPending && isFetching`, so it is FALSE for a query that is
   * pending but not in flight. react-query pauses a fetch while `onlineManager`
   * believes the browser is offline (the default `networkMode: 'online'`):
   * `queryFn` is never called, nothing throws, nothing is logged, and `data` stays
   * undefined. The old `data?.data || []` then rendered "No users found" — on the
   * screen an administrator uses to see who has an account and to reset passwords.
   * "This firm has no users" is a claim; the truth was that nobody ever asked.
   *
   * Hooks must run before the early returns below, so this sits with the query and
   * not next to the branch that consumes it.
   */
  const usersState = useReadState('settings-users', usersQuery)
  const usersReady = usersState === 'ready'

  const resetMutation = useMutation({
    mutationFn: ({ userId, password, userName }) => userService.resetPassword(userId, password).then(() => userName),
    onSuccess: (userName) => {
      setMessage({ type: 'success', text: `Password reset for ${userName}` })
      setResetTarget(null)
      setPassword('')
      setConfirm('')
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message })
    },
  })

  const closeModal = () => {
    setResetTarget(null)
    setPassword('')
    setConfirm('')
  }

  const handleReset = (e) => {
    e.preventDefault()
    if (password !== confirm) {
      setMessage({ type: 'error', text: 'Passwords do not match' })
      return
    }
    if (password.length < 4) {
      setMessage({ type: 'error', text: 'Password must be at least 4 characters' })
      return
    }
    resetMutation.mutate({ userId: resetTarget.id, password, userName: resetTarget.name })
  }

  // A 403 is an authorization answer, not a failed read — the section simply does
  // not apply to this user, so there is nothing to tell them about.
  const is403 = error?.response?.status === 403
  if (is403) return null

  /* `idle` rides with `loading` on purpose. It means pending-but-never-started
     (`enabled: false`), which this query cannot reach today — it has no `enabled`
     option. But the loading branch renders NOTHING, so an unhandled state would
     make the whole card vanish silently and then fall through to the table below.
     Cheaper to cover the trap than to leave it for whoever adds a gate here. */
  if (usersState === 'loading' || usersState === 'idle') return null

  const rows = data?.data
  const users = Array.isArray(rows) ? rows : []
  // A 200 whose body is not an array is a failed read dressed as an empty firm.
  // `userService` has no `requireArrayIn` guard (`@api/v1/readShape`) yet, so hold
  // the line here rather than letting `|| []` render it as "No users found".
  const shapeUnreadable = usersReady && !Array.isArray(rows)

  /* The state that used to render as "No users found". A paused fetch never
     resolves on its own, so a spinner would sit here for as long as the browser
     stayed offline, and the old code did not even show one — it showed an empty
     firm. `role="alert"` because the user has to be told the screen is not
     describing their data. The button borders are a darker tint than the panel's:
     an outlined control's border is the only thing identifying it as a control, so
     it needs 3.0:1 (WCAG 1.4.11) and amber-300/red-200 do not clear it. */
  if (usersState === 'blocked') {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">User Management</h2>
        </div>
        <div className="p-6">
          <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <p className="font-medium">Can’t reach the server, so we haven’t loaded your users.</p>
            <p className="mt-1 text-amber-800">
              This is not an empty user list — no account has been removed and no password has
              changed. Your connection looks offline; check it and try again.
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-3 rounded-lg border border-amber-600 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Any other error. Already distinguishable from empty before this change; it
  // gains `role="alert"` and a retry so the user is not left reloading the app.
  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">User Management</h2>
        </div>
        <div className="p-6">
          <div role="alert" className="rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
            <p className="font-medium">Failed to load users: {error.response?.data?.error || error.message}</p>
            <p className="mt-1">This is not an empty user list.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-3 rounded-lg border border-red-500 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (shapeUnreadable) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">User Management</h2>
        </div>
        <div className="p-6">
          <div role="alert" className="rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
            <p className="font-medium">The user list came back in a shape we couldn’t read.</p>
            <p className="mt-1">
              This is not an empty user list — try again, and report it if it persists.
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-3 rounded-lg border border-red-500 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900">User Management</h2>
        <p className="text-xs text-gray-500 mt-0.5">Manage tenant users and reset passwords</p>
      </div>
      <div className="p-6">
        {message && (
          <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase">Name</th>
                <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase">Email</th>
                <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-right py-2 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-2.5 pr-4 text-gray-900">{u.name}</td>
                  <td className="py-2.5 pr-4 text-gray-600">{u.email || '—'}</td>
                  <td className="py-2.5 pr-4">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      u.status === 'Inactive'
                        ? 'bg-gray-100 text-gray-600'
                        : 'bg-green-50 text-green-700'
                    }`}>
                      {u.status || 'Active'}
                    </span>
                  </td>
                  <td className="py-2.5 text-right">
                    <button
                      onClick={() => { setResetTarget(u); setMessage(null) }}
                      className="text-xs text-cb-accent-text hover:text-cb-navy font-medium"
                    >
                      Reset Password
                    </button>
                  </td>
                </tr>
              ))}
              {/* `usersReady` is redundant here today — every other state returned
                  above — and stays anyway: it is the invariant that makes this row
                  a genuine observation rather than a fallback, and it survives
                  someone deleting one of those early returns. */}
              {usersReady && users.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-gray-500">No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reset Password Modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">Reset Password</h3>
              <p className="text-xs text-gray-500 mt-0.5">Set a new password for {resetTarget.name}</p>
            </div>
            <form onSubmit={handleReset} className="p-6 space-y-4">
              {resetMutation.isError && (
                <div className="px-4 py-2 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200">
                  {resetMutation.error?.response?.data?.error || resetMutation.error?.message || 'Password reset failed'}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent"
                  autoFocus
                  required
                  minLength={4}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent"
                  required
                  minLength={4}
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={resetMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-cb-navy rounded-lg hover:bg-cb-navy/90 disabled:opacity-50"
                >
                  {resetMutation.isPending ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

const InfoField = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
    <p className="text-sm text-gray-900">{value || '—'}</p>
  </div>
)

export default Settings
